import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Output,
  OnInit,
  Input,
  ChangeDetectorRef,
} from '@angular/core';
import { TopicTypeId } from '@app/origami-api/constants';
import { TopicSearchService } from '@app/origami-api/services/topic-search.service';
import { TopicUtils, Topic } from '@app/origami-api/utils/topic-utils';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ConfigService, ConfigServiceOptions } from '@app/origami-api/services/config.service';
import { ConfidentialityAgreementFields } from '@app/services/settings/modules/confidentiality-agreement';
import { take } from 'rxjs/operators';
import { zip } from 'rxjs';
import { SelectEvent } from '@progress/kendo-angular-layout';

interface Agreement {
  id: number;
  name: string;
  html: SafeHtml;
}

@Component({
  standalone: false,
  selector: 'ti-confidentiality-agreement',
  templateUrl: './confidentiality-agreement.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConfidentialityAgreementComponent implements OnInit {
  public readonly strings = tiStrings.shared.confidentialityAgreement;
  miscStrings = tiStrings.app.misc;

  @Input() roomId: number;
  @Output() close = new EventEmitter<number>();
  agreements: Agreement[];
  selectedAgreement: Agreement;
  isLoading: boolean;
  defaultCAId: number;

  title: string = this.strings.title;

  constructor(
    private topicSearchService: TopicSearchService,
    private sanitizer: DomSanitizer,
    private configService: ConfigService,
    private cd: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    const modules = [ConfigServiceOptions.ModuleName.ConfidentialityAgreement];
    const settings = [ConfidentialityAgreementFields.CommonField.Default];
    const searchTopicsObs = this.topicSearchService.searchTopics({
      siteId: this.roomId,
      topicTypeId: TopicTypeId.ConfidentialityAgreement,
    });
    const getSettingsObs = this.configService.getSettings(this.roomId, modules, settings);
    zip(searchTopicsObs, getSettingsObs)
      .pipe(take(1))
      .subscribe(
        ([searchTopicsRes, getSettingsRes]) => {
          if (getSettingsRes.Result && getSettingsRes.Result[0] && getSettingsRes.Result[0].TopicFields) {
            this.defaultCAId = +TopicUtils.getTopicFieldValue(
              getSettingsRes.Result[0].TopicFields,
              ConfidentialityAgreementFields.CommonField.Default,
            );
          }
          this.updateAgreements(searchTopicsRes.Result.TopicList || []);
        },
        () => this.updateAgreements([]),
      );
  }

  onTabSelect(data: SelectEvent) {
    this.selectedAgreement = this.agreements[data.index];
  }

  agree() {
    this.selectedAgreement = this.selectedAgreement || this.agreements[0];
    this.isLoading = true;
    this.close.emit(this.selectedAgreement.id);
  }

  decline() {
    this.close.emit(null);
  }

  private updateAgreements(topics: Topic[]) {
    this.agreements = topics.map((topic) => ({
      id: topic.TopicId,
      name: topic.TopicName,
      html: this.sanitizer.bypassSecurityTrustHtml(TopicUtils.getTopicFieldValue(topic, 'CAText') || ''),
    }));

    if (this.agreements.length === 1) {
      this.title = this.agreements[0].name;
    }

    this.cd.detectChanges();
  }
}
